/**
 * Trilha de rock em camadas, sintetizada no próprio jogo (pedido do Matheus em 19/10):
 *
 *   1 Calmaria   — só violão dedilhado.
 *   2 Esquentando — entram baixo e bateria leve.
 *   3 Treta      — guitarras pesadas em cavalgada (o "galope" do metal dos anos 80), bateria cheia.
 *   4 Caos final — solo de guitarra rápido e pesado, com duas guitarras em harmonia.
 *
 * Música original em mi menor (Em – C – D – Em | Em – C – D – B), 150 batidas por minuto.
 * Os instrumentos de corda usam o algoritmo de Karplus-Strong (corda dedilhada de verdade,
 * calculada uma vez quando a trilha começa); a guitarra passa por distorção e "caixa de som".
 */

type Level = 1 | 2 | 3 | 4;

const SR_NOTE = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

export const ROCK_BPM = 150;
/** 8 compassos de 16 semicolcheias. */
export const ROCK_STEPS = 128;

const CHORDS: { root: number; ac: number[]; power: number[] }[] = [
  { root: 40, ac: [40, 47, 52, 55, 59, 64], power: [40, 47, 52] }, // Em
  { root: 36, ac: [48, 52, 55, 60, 64], power: [48, 55, 60] }, // C
  { root: 38, ac: [50, 57, 62, 66], power: [50, 57, 62] }, // D
  { root: 40, ac: [40, 47, 52, 55, 59, 64], power: [40, 47, 52] }, // Em
  { root: 40, ac: [40, 47, 52, 55, 59, 64], power: [40, 47, 52] }, // Em
  { root: 36, ac: [48, 52, 55, 60, 64], power: [48, 55, 60] }, // C
  { root: 38, ac: [50, 57, 62, 66], power: [50, 57, 62] }, // D
  { root: 35, ac: [47, 51, 57, 59, 63], power: [47, 54, 59] }, // B7
];

/** Volume de cada camada (violão, baixo+bateria, guitarras base, solo) em cada nível de tensão. */
const MIX: Record<Level, [number, number, number, number]> = {
  1: [1.9, 0, 0, 0],
  2: [1.6, 1, 0, 0],
  3: [0, 0.9, 0.55, 0],
  4: [0, 0.9, 0.45, 0.6],
};

/** Nota de solo: [passo no compasso, nota MIDI, duração em passos, efeito]. */
type LeadNote = [number, number, number, ('bend' | 'vib' | '')?];

/** O solo (8 compassos). Frases longas em harmonia nas metades lentas, escalas rápidas nas outras. */
const SOLO_A: LeadNote[][] = [
  // Em
  [[0, 71, 2], [2, 76, 1], [3, 76, 1], [4, 78, 2], [6, 79, 2], [8, 78, 1], [9, 79, 1], [10, 81, 2], [12, 79, 4, 'vib']],
  // C
  [[0, 79, 2], [2, 76, 1], [3, 79, 1], [4, 84, 2], [6, 83, 2], [8, 81, 1], [9, 79, 1], [10, 81, 2], [12, 76, 4, 'vib']],
  // D
  [[0, 78, 2], [2, 74, 1], [3, 78, 1], [4, 81, 2], [6, 79, 2], [8, 78, 1], [9, 76, 1], [10, 78, 2], [12, 74, 4, 'vib']],
  // Em
  [[0, 76, 1], [1, 78, 1], [2, 79, 1], [3, 81, 1], [4, 83, 2], [6, 81, 1], [7, 79, 1], [8, 78, 1], [9, 76, 1], [10, 74, 1], [11, 71, 1], [12, 74, 4, 'bend']],
];

const run = (notes: number[], fx?: LeadNote[3]): LeadNote[] => notes.map((n, i) => [i, n, 1, i === notes.length - 1 ? fx : '']);

const SOLO_B: LeadNote[][] = [
  // Em: escala subindo e voltando
  run([64, 66, 67, 69, 71, 72, 74, 76, 74, 72, 71, 69, 71, 72, 74, 76]),
  // C: arpejo varrido
  run([72, 76, 79, 84, 79, 76, 72, 76, 79, 84, 88, 84, 79, 76, 79, 84]),
  // D
  [...run([74, 78, 81, 86, 81, 78, 74, 78]), [8, 81, 1], [9, 83, 1], [10, 86, 2], [12, 88, 4, 'bend']],
  // B7 (menor harmônica: ré sustenido)
  [...run([71, 75, 78, 83, 78, 75, 71, 75]), [8, 78, 8, 'vib']],
];

/** Escala de mi menor (natural) e harmônica, para a segunda guitarra tocar uma terça acima. */
const E_MINOR = [4, 6, 7, 9, 11, 0, 2];
const E_HARMONIC = [4, 6, 7, 9, 11, 0, 3];
function thirdAbove(midi: number, harmonic: boolean): number {
  const scale = harmonic ? E_HARMONIC : E_MINOR;
  const pc = ((midi % 12) + 12) % 12;
  let idx = scale.indexOf(pc);
  if (idx < 0) idx = Math.max(0, scale.findIndex((s) => s > pc));
  const target = scale[(idx + 2) % 7]!;
  let up = (target - pc + 12) % 12;
  if (up === 0) up = 12;
  return midi + up;
}

interface Chain {
  input: GainNode;
}

export class RockBand {
  private ctx: BaseAudioContext;
  private noise: AudioBuffer;
  readonly layers: GainNode[] = [];
  private ac!: Chain;
  private bass!: Chain;
  private drums!: GainNode;
  private rhy: Chain[] = [];
  private lead!: Chain;
  private harm!: Chain;
  private cache = new Map<string, AudioBuffer>();
  private pass = 0;
  level: Level = 1;

  constructor(ctx: BaseAudioContext, out: AudioNode, noise: AudioBuffer) {
    this.ctx = ctx;
    this.noise = noise;
    const verb = ctx.createConvolver();
    verb.buffer = this.impulse(1.6);
    const verbOut = ctx.createGain();
    verbOut.gain.value = 0.9;
    verb.connect(verbOut).connect(out);
    const sends = [0.28, 0.06, 0.05, 0.22];
    for (let k = 0; k < 4; k++) {
      const g = ctx.createGain();
      g.gain.value = 0;
      g.connect(out);
      const s = ctx.createGain();
      s.gain.value = sends[k]!;
      g.connect(s).connect(verb);
      this.layers.push(g);
    }
    const [lAc, lBand, lHeavy, lLead] = this.layers as [GainNode, GainNode, GainNode, GainNode];

    // Violão: corpo de madeira (graves cheios e brilho suave).
    this.ac = this.chain(lAc, [
      ['highpass', 70, 0.7, 0],
      ['peaking', 190, 1, 3],
      ['peaking', 2600, 1, 2],
      ['lowpass', 9000, 0.7, 0],
    ]);
    // Baixo: um pouco saturado para aparecer até no alto-falante do celular.
    this.bass = this.chain(lBand, [['lowpass', 1600, 0.8, 0]], 1.8, 0.75);
    this.drums = ctx.createGain();
    this.drums.gain.value = 0.9;
    this.drums.connect(lBand);
    // Guitarras base: duas gravações (esquerda e direita) com distorção pesada.
    for (const pan of [-0.65, 0.65]) this.rhy.push(this.guitar(lHeavy, pan, 2800, 26, 0.33));
    // Solo e a segunda voz (harmonia).
    this.lead = this.guitar(lLead, -0.15, 7000, 40, 0.3, true);
    this.harm = this.guitar(lLead, 0.35, 7000, 40, 0.22, true);
  }

  get stepDur(): number {
    return 60 / ROCK_BPM / 4;
  }

  /** Volume de cada camada para o nível (com transição suave, `fade` em segundos). */
  setLevel(level: Level, t: number, fade = 1.2): void {
    this.level = level;
    this.layers.forEach((g, i) => {
      if (fade <= 0) g.gain.setValueAtTime(MIX[level][i]!, t);
      else g.gain.setTargetAtTime(MIX[level][i]!, t, fade);
    });
  }

  /** Calcula as cordas de antemão (uns poucos milissegundos), para não engasgar durante o jogo. */
  warm(): void {
    for (const c of CHORDS) {
      for (const n of c.ac) this.pluck('ac', n);
      for (const n of c.power) this.pluck('rhy', n);
      this.pluck('bass', c.root + 12);
      this.pluck('bass', c.root + 24);
    }
    for (const bar of [...SOLO_A, ...SOLO_B])
      for (const [, n] of bar) {
        this.pluck('lead', n);
        this.pluck('lead', thirdAbove(n, false));
        this.pluck('lead', thirdAbove(n, true));
      }
  }

  playStep(s: number, t: number): void {
    const bar = Math.floor(s / 16) % 8;
    const i = s % 16;
    if (s === 0) this.pass++;
    const c = CHORDS[bar]!;
    const L = this.level;
    const d = this.stepDur;

    // 1. Violão dedilhado (colcheias): baixo, cordas do meio e agudas, e volta.
    if (L <= 2 && i % 2 === 0) {
      const v = c.ac;
      const order = [0, 2, 3, v.length - 1, 3, 2, 1, 3];
      const n = v[Math.min(order[i / 2]!, v.length - 1)]!;
      this.play('ac', n, t, 2.2, i === 0 ? 0.55 : 0.36, this.ac.input);
      if (L === 2 && (i === 4 || i === 12)) for (const k of v.slice(-3)) this.play('ac', k, t + 0.008, 0.6, 0.12, this.ac.input);
    }

    // 2. Baixo e bateria.
    if (L >= 2) {
      if (L === 2) {
        if (i % 4 === 0) this.play('bass', c.root + 12, t, d * 3.5, 0.55, this.bass.input);
        if (i === 0 || i === 8 || i === 10) this.kick(t, 0.7);
        if (i === 4 || i === 12) this.snare(t, 0.35);
        if (i % 2 === 0) this.hat(t, i % 4 === 0 ? 0.06 : 0.1, 0.04);
      } else {
        // Cavalgada: colcheia + duas semicolcheias.
        const gallop = i % 4 !== 1;
        if (gallop) this.play('bass', c.root + 12, t, d * (i % 4 === 0 ? 0.95 : 0.85), i % 4 === 0 ? 0.7 : 0.55, this.bass.input);
        if (gallop) this.kick(t, i % 4 === 0 ? 0.95 : 0.7);
        if (i === 4 || i === 12) this.snare(t, 0.85);
        if (i % 2 === 0) this.hat(t, 0.12, i % 4 === 2 ? 0.12 : 0.05);
        if (i === 0 && bar % 2 === 0) this.crash(t, 0.5);
        if (bar === 7 && i >= 8 && i % 2 === 0) this.tom(t, [190, 150, 120, 95][(i - 8) / 2]!);
      }
    }

    // 3. Guitarras base: acordes de quinta abafados (palm mute) no galope.
    if (L >= 3) {
      const gallop = i % 4 !== 1;
      const accent = i % 4 === 0;
      const long = bar === 7 && i === 0;
      if (gallop || long) {
        this.rhy.forEach((ch, k) => {
          const delay = k * 0.011;
          for (const n of c.power) this.play('rhy', n, t + delay, long ? d * 7 : accent ? d * 0.95 : d * 0.75, accent ? 0.5 : 0.38, ch.input);
        });
      }
    }

    // 4. Solo com harmonia.
    if (L >= 4) {
      const half = this.pass % 2 === 0 ? [SOLO_A, SOLO_B] : [SOLO_B, SOLO_A];
      const phrase = bar < 4 ? half[0]![bar]! : half[1]![bar - 4]!;
      const isSlow = phrase === SOLO_A[bar % 4];
      for (const [step, n, len, fx] of phrase) {
        if (step !== i) continue;
        this.leadNote(this.lead.input, n, t, len * d, fx ?? '', 0.42);
        // A segunda guitarra harmoniza as frases cantadas (estilo duas guitarras em terças).
        if (isSlow || bar === 7) this.leadNote(this.harm.input, thirdAbove(n, bar === 7), t + 0.004, len * d, fx ?? '', 0.3);
      }
    }
  }

  // ------------------------------------------------------------------ instrumentos

  private chain(out: AudioNode, eq: [BiquadFilterType, number, number, number][], drive = 0, level = 1): Chain {
    const ctx = this.ctx;
    const input = ctx.createGain();
    let node: AudioNode = input;
    if (drive > 0) {
      const sh = ctx.createWaveShaper();
      sh.curve = curve(drive);
      sh.oversample = '2x';
      node = node.connect(sh);
    }
    for (const [type, f, q, gain] of eq) {
      const b = ctx.createBiquadFilter();
      b.type = type;
      b.frequency.value = f;
      b.Q.value = q;
      b.gain.value = gain;
      node = node.connect(b);
    }
    const g = ctx.createGain();
    g.gain.value = level;
    node.connect(g).connect(out);
    return { input };
  }

  /** Guitarra elétrica: pré-filtro, distorção, "caixa" (filtros) e posição no estéreo. */
  private guitar(out: AudioNode, pan: number, pre: number, drive: number, level: number, delay = false): Chain {
    const ctx = this.ctx;
    const input = ctx.createGain();
    const preF = ctx.createBiquadFilter();
    preF.type = 'lowpass';
    preF.frequency.value = pre;
    const sh = ctx.createWaveShaper();
    sh.curve = curve(drive);
    sh.oversample = '4x';
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 95;
    const mid = ctx.createBiquadFilter();
    mid.type = 'peaking';
    mid.frequency.value = 1100;
    mid.Q.value = 0.9;
    mid.gain.value = 4;
    const scoop = ctx.createBiquadFilter();
    scoop.type = 'peaking';
    scoop.frequency.value = 420;
    scoop.Q.value = 1;
    scoop.gain.value = -3;
    const cab = ctx.createBiquadFilter();
    cab.type = 'lowpass';
    cab.frequency.value = 5200;
    cab.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.value = level;
    const p = ctx.createStereoPanner();
    p.pan.value = pan;
    input.connect(preF).connect(sh).connect(hp).connect(scoop).connect(mid).connect(cab).connect(g).connect(p).connect(out);
    if (delay) {
      const dl = ctx.createDelay(1);
      dl.delayTime.value = (60 / ROCK_BPM) * 0.75;
      const fb = ctx.createGain();
      fb.gain.value = 0.28;
      const wet = ctx.createGain();
      wet.gain.value = 0.22;
      p.connect(dl);
      dl.connect(fb).connect(dl);
      dl.connect(wet).connect(out);
    }
    return { input };
  }

  /** Uma nota de corda: amostra calculada + envelope (abafa no fim). */
  private play(kind: 'ac' | 'rhy' | 'bass' | 'lead', midi: number, t: number, dur: number, vel: number, out: AudioNode): AudioBufferSourceNode {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.pluck(kind, midi);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vel, t);
    g.gain.setValueAtTime(vel, t + Math.max(0.01, dur - 0.03));
    g.gain.linearRampToValueAtTime(0, t + dur + 0.04);
    src.connect(g).connect(out);
    src.start(t);
    src.stop(t + dur + 0.06);
    return src;
  }

  private leadNote(out: AudioNode, midi: number, t: number, dur: number, fx: string, vel: number): void {
    const src = this.play('lead', midi, t, dur, vel, out);
    const rate = src.playbackRate;
    if (fx === 'bend') {
      // Começa um tom abaixo e sobe até a nota (bend), depois vibra.
      rate.setValueAtTime(2 ** (-2 / 12), t);
      rate.linearRampToValueAtTime(1, t + Math.min(0.18, dur * 0.4));
    }
    if (fx === 'vib' || fx === 'bend') {
      const lfo = this.ctx.createOscillator();
      lfo.frequency.value = 6;
      const depth = this.ctx.createGain();
      depth.gain.setValueAtTime(0, t);
      depth.gain.linearRampToValueAtTime(0.012, t + dur * 0.5);
      lfo.connect(depth).connect(rate);
      lfo.start(t);
      lfo.stop(t + dur + 0.06);
    }
  }

  /** Corda dedilhada (Karplus-Strong com afinação fracionária e posição da palheta). */
  private pluck(kind: 'ac' | 'rhy' | 'bass' | 'lead', midi: number): AudioBuffer {
    const key = `${kind}:${midi}`;
    const hit = this.cache.get(key);
    if (hit) return hit;
    const sr = this.ctx.sampleRate;
    const f = SR_NOTE(midi);
    const cfg = {
      ac: { secs: 2.4, t60: 2.6, bright: 0.55, pick: 0.18 },
      rhy: { secs: 1.2, t60: 1.6, bright: 0.85, pick: 0.12 },
      bass: { secs: 1.6, t60: 1.8, bright: 0.35, pick: 0.25 },
      lead: { secs: 2.6, t60: 6, bright: 0.9, pick: 0.1 },
    }[kind];
    const n = Math.ceil(sr * cfg.secs);
    const y = new Float32Array(n);
    const P = sr / f;
    const D = P - 0.5; // o filtro de média atrasa meio passo
    const exc = Math.ceil(P) + 2;
    let lp = 0;
    let mean = 0;
    for (let k = 0; k < exc; k++) {
      lp += cfg.bright * (Math.random() * 2 - 1 - lp);
      y[k] = lp;
      mean += lp;
    }
    mean /= exc;
    const pickAt = Math.max(1, Math.floor(P * cfg.pick));
    for (let k = exc - 1; k >= 0; k--) y[k] = y[k]! - mean - (k >= pickAt ? y[k - pickAt]! - mean : 0);
    const decay = Math.exp(Math.log(0.001) / (cfg.t60 * f));
    for (let k = exc; k < n; k++) {
      const pos = k - D;
      const i0 = Math.floor(pos);
      const fr = pos - i0;
      const a = y[i0]! * (1 - fr) + y[i0 + 1]! * fr;
      const b = y[i0 - 1]! * (1 - fr) + y[i0]! * fr;
      y[k] = decay * 0.5 * (a + b);
    }
    // Normaliza para todas as notas soarem com a mesma força.
    let peak = 0;
    for (let k = 0; k < Math.min(n, exc * 4); k++) peak = Math.max(peak, Math.abs(y[k]!));
    const norm = peak > 0 ? 0.9 / peak : 1;
    const buf = this.ctx.createBuffer(1, n, sr);
    const ch = buf.getChannelData(0);
    for (let k = 0; k < n; k++) ch[k] = y[k]! * norm;
    this.cache.set(key, buf);
    return buf;
  }

  // ------------------------------------------------------------------ bateria

  private kick(t: number, vel: number): void {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.setValueAtTime(140, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.12);
    g.gain.setValueAtTime(vel, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    o.connect(g).connect(this.drums);
    o.start(t);
    o.stop(t + 0.25);
    this.noiseHit(t, 0.012, vel * 0.35, 'highpass', 2500);
  }

  private snare(t: number, vel: number): void {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(220, t);
    o.frequency.exponentialRampToValueAtTime(160, t + 0.08);
    g.gain.setValueAtTime(vel * 0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    o.connect(g).connect(this.drums);
    o.start(t);
    o.stop(t + 0.14);
    this.noiseHit(t, 0.18, vel * 0.55, 'bandpass', 2400, 0.6);
  }

  private hat(t: number, vel: number, len: number): void {
    this.noiseHit(t, len, vel, 'highpass', 8000);
  }

  private crash(t: number, vel: number): void {
    this.noiseHit(t, 1.6, vel * 0.5, 'highpass', 5000);
  }

  private tom(t: number, f: number): void {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.setValueAtTime(f, t);
    o.frequency.exponentialRampToValueAtTime(f * 0.6, t + 0.2);
    g.gain.setValueAtTime(0.7, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
    o.connect(g).connect(this.drums);
    o.start(t);
    o.stop(t + 0.3);
  }

  private noiseHit(t: number, dur: number, vel: number, type: BiquadFilterType, f: number, q = 0.8): void {
    const ctx = this.ctx;
    const s = ctx.createBufferSource();
    s.buffer = this.noise;
    const flt = ctx.createBiquadFilter();
    flt.type = type;
    flt.frequency.value = f;
    flt.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vel, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    s.connect(flt).connect(g).connect(this.drums);
    s.start(t, Math.random() * 0.5);
    s.stop(t + dur + 0.02);
  }

  /** Reverberação de sala (ruído que vai sumindo). */
  private impulse(secs: number): AudioBuffer {
    const sr = this.ctx.sampleRate;
    const n = Math.floor(sr * secs);
    const buf = this.ctx.createBuffer(2, n, sr);
    for (let c = 0; c < 2; c++) {
      const d = buf.getChannelData(c);
      for (let k = 0; k < n; k++) d[k] = (Math.random() * 2 - 1) * (1 - k / n) ** 3;
    }
    return buf;
  }
}

/** Curva de distorção (saturação suave que vira "pedal de metal" com muito ganho). */
function curve(drive: number): Float32Array<ArrayBuffer> {
  const n = 2048;
  const c = new Float32Array(new ArrayBuffer(n * 4));
  const norm = Math.tanh(drive);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.tanh(drive * x) / norm;
  }
  return c;
}
